'use client';

import { ReactElement, useMemo } from 'react';
import { Box, Center, Icon, VStack } from '@chakra-ui/react';
import {
  MdAccountBalance,
  MdCardGiftcard,
  MdChatBubbleOutline,
} from 'react-icons/md';
import { TbArrowsRightLeft } from 'react-icons/tb';
import { FiMinusCircle, FiSave } from 'react-icons/fi';
import { isBefore, subDays } from 'date-fns';

import storeIcon from 'assets/icons/store-icon.svg';
import { Paths, ZmyleLinks } from 'utils/paths';
import {
  doesRouteMatch,
  parseDateFromServer,
  resolvePath,
} from 'utils/helpers';
import { useNetwork } from 'context/network';
import { useRouter } from 'hooks/useRouter';
import { useGiftcard } from 'context/giftcard';
import { useGiftcardsPagesLayoutContent } from 'context/giftcardsPagesLayoutContent';
import { Link, LinkProps } from '../Link';
import { Button } from '../Button';

const linkBaseStyle: Partial<LinkProps> = {
  whileHover: {},
  whileTap: {},
} as const;

const activeLinkStyle = {
  animate: { scale: 1.05 },
  shadow: 'dark-lg',
} as const;

interface ActionButtonLinkProps extends LinkProps {
  isActive: boolean;
}

type ActionButtonName =
  | 'details'
  | 'message'
  | 'outlets'
  | 'transactions'
  | 'offerRedeem'
  | 'saveGiftcard'
  | 'account';

interface ActionButton {
  name: ActionButtonName;
  text: string;
  linkProps?: ActionButtonLinkProps;
  icon: ReactElement;
}

interface RouteParams {
  t?: string;
  rt?: string;
  giftcardId?: string;
}

interface Props {
  renderOnly?: ActionButtonName[];
}

export const GiftcardActionButtons = (props: Props) => {
  const { renderOnly = [] } = props;

  const { network } = useNetwork();

  const { query: routerQuery, pathname } = useRouter<RouteParams>();

  const { giftcard } = useGiftcard();

  const { giftcardsActionsButtons: content } = useGiftcardsPagesLayoutContent();

  const {
    details,
    message,
    transactions,
    outlets,
    saveGiftcard,
    cancel,
    account,
  } = content;

  const query = {
    giftcardId: String(routerQuery?.giftcardId),
    t: routerQuery?.t ? String(routerQuery?.t) : undefined,
    rt: routerQuery?.rt ? String(routerQuery?.rt) : undefined,
  };

  const hasMessage = Boolean(giftcard.message.text || giftcard.message.media);

  const hasFreshMessage = useMemo(() => {
    if (!hasMessage || !giftcard.paidAt) return false;

    const parsedPaidAt = parseDateFromServer(giftcard.paidAt);

    if (!parsedPaidAt) return false;

    const fourDaysAgo = subDays(new Date(), 4);

    return isBefore(fourDaysAgo, parsedPaidAt);
  }, [giftcard]);

  const detailsLinkProps = useMemo<ActionButtonLinkProps>(() => {
    return {
      ...linkBaseStyle,
      isActive: doesRouteMatch({
        route: pathname,
        signature: Paths.GIFTCARD_SECRET_PAGE,
      }),
      href: resolvePath({ pathname: Paths.GIFTCARD_SECRET_PAGE, query }),
    };
  }, []);

  const messageLinkProps = useMemo<ActionButtonLinkProps>(() => {
    return {
      ...linkBaseStyle,
      isActive: doesRouteMatch({
        route: pathname,
        signature: Paths.GIFTCARD_MESSAGE_PAGE,
      }),
      href: resolvePath({ pathname: Paths.GIFTCARD_MESSAGE_PAGE, query }),
    };
  }, []);

  const outletsLinkProps = useMemo<ActionButtonLinkProps>(() => {
    const resolveHref = () => {
      const { store, purchasedForRetailerId, infoUrl } = giftcard;

      if (!purchasedForRetailerId || !store) return ZmyleLinks.OUTLETS;

      if (!infoUrl) {
        return `${network?.slugFqdn}${ZmyleLinks.STORES}${store.zmyleUri}`;
      }

      const url = new URL(infoUrl);

      return `${url.origin}${ZmyleLinks.STORES}${store.zmyleUri}`;
    };

    const href = resolveHref();

    return {
      ...linkBaseStyle,
      isActive: doesRouteMatch({ route: pathname, signature: href }),
      href,
      isExternal: true,
      target: '_blank',
    };
  }, [network]);

  const transactionsLinkProps = useMemo<ActionButtonLinkProps>(() => {
    return {
      ...linkBaseStyle,
      isActive: doesRouteMatch({
        route: pathname,
        signature: Paths.GIFTCARD_TRANSACTIONS,
      }),
      href: resolvePath({ pathname: Paths.GIFTCARD_TRANSACTIONS, query }),
    };
  }, []);

  const saveGiftcardLinkProps = useMemo<ActionButtonLinkProps>(() => {
    return {
      ...linkBaseStyle,
      isActive: doesRouteMatch({
        route: pathname,
        signature: Paths.GIFTCARD_DOWNLOAD_PAGE,
      }),
      href: resolvePath({ pathname: Paths.GIFTCARD_DOWNLOAD_PAGE, query }),
    };
  }, []);

  const offerRedeemLinkProps = useMemo<ActionButtonLinkProps>(() => {
    return {
      ...linkBaseStyle,
      isActive: doesRouteMatch({
        route: pathname,
        signature: Paths.GIFTCARD_OFFER_REDEEM,
      }),
      href: resolvePath({ pathname: Paths.GIFTCARD_OFFER_REDEEM, query }),
    };
  }, []);

  const giftcardDepotLinkProps = useMemo<ActionButtonLinkProps>(() => {
    return {
      ...linkBaseStyle,
      isActive: doesRouteMatch({
        route: pathname,
        signature: Paths.GIFTCARDS_DEPOT_PAGE,
      }),
      href: resolvePath({ pathname: Paths.GIFTCARDS_DEPOT_PAGE, query }),
    };
  }, []);

  const allActionButtons: ActionButton[] = [
    {
      name: 'details',
      text: details,
      linkProps: detailsLinkProps,
      icon: (
        <Icon as={MdCardGiftcard} fontSize="1.75rem" color="dynamic.accent" />
      ),
    },
    {
      name: 'message',
      text: message,
      linkProps: messageLinkProps,
      icon: (
        <Center position="relative" h="full">
          <Box
            w="0.65rem"
            h="0.65rem"
            border="1px solid white"
            pos="absolute"
            right="-0.125rem"
            top="-0.125rem"
            bg="red"
            rounded="full"
            visibility={hasFreshMessage ? 'visible' : 'hidden'}
          />

          <Icon
            as={MdChatBubbleOutline}
            fontSize="1.75rem"
            color="dynamic.accent"
          />
        </Center>
      ),
    },
    {
      name: 'transactions',
      text: transactions,
      linkProps: transactionsLinkProps,
      icon: (
        <Icon
          as={TbArrowsRightLeft}
          fontSize="1.75rem"
          color="dynamic.accent"
        />
      ),
    },
    {
      name: 'outlets',
      text: outlets,
      linkProps: outletsLinkProps,
      icon: <Icon as={storeIcon} fill="dynamic.accent" fontSize="1.5rem" />,
    },
    {
      name: 'saveGiftcard',
      text: saveGiftcard,
      linkProps: saveGiftcardLinkProps,
      icon: <Icon as={FiSave} fontSize="1.75rem" color="dynamic.accent" />,
    },
    {
      name: 'offerRedeem',
      text: cancel,
      linkProps: offerRedeemLinkProps,
      icon: (
        <Icon as={FiMinusCircle} fontSize="1.75rem" color="dynamic.accent" />
      ),
    },
    {
      name: 'account',
      text: account,
      linkProps: giftcardDepotLinkProps,
      icon: (
        <Icon as={MdAccountBalance} fontSize="1.75rem" color="dynamic.accent" />
      ),
    },
  ];

  const actionButtons = allActionButtons.filter(button => {
    if (!hasMessage && button.name === 'message') return false;

    if (button.name === 'account' && !giftcard.giftcardDepotId) return false;

    if (renderOnly.length) return renderOnly.includes(button.name);

    return true;
  });

  const renderActionButton = (button: ActionButton) => {
    if (button.linkProps) {
      const { isActive, ...restLinkProps } = button.linkProps;

      const activeStyle = isActive ? activeLinkStyle : {};

      if (isActive) {
        return (
          <Button
            key={button.name}
            leftIcon={button.icon}
            variant="shadow"
            isInteractivityHighlightEnabled={false}
            {...activeStyle}
          >
            {button.text}
          </Button>
        );
      }

      return (
        <Link key={button.name} w="full" {...restLinkProps} {...activeStyle}>
          <Button leftIcon={button.icon} variant="shadow">
            {button.text}
          </Button>
        </Link>
      );
    }

    return (
      <Button key={button.name} leftIcon={button.icon} variant="shadow">
        {button.text}
      </Button>
    );
  };

  return (
    <VStack w="full" spacing="1.5rem" data-testid="giftcard-action-buttons">
      {actionButtons.map(renderActionButton)}
    </VStack>
  );
};
